import { getRuntimeValue, setSharedRuntimeContext, subscribeRuntimeContext } from "@3sc/common-component";
import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { readAppValue, setAppRuntimeContext } from "../config/runtimeAccessor";
import { getSessionProfile, handleAuthError, refreshAccessToken } from "../helper/authService";
import { findDefaultBu, userObj } from "../helper/commonHelper";
import { useTabScopedIdleLogout } from "../hooks/useTabScopedIdleLogout";
import { getActiveSnop, getConfiguration, getUserSettings } from "../utils/ApiHandler";
import { ROUTES_PATH } from "../utils/EndPoints";
import { ENUM_EVENT } from "../utils/eventEmitter";

const AuthContext = createContext(null);

const DEFAULT_SETTINGS = { platform: { theme: "light" } };

// dpai, snoe, dsai and snp don't own their own active-snop fetch, so this
// app is their single owner — fetch it only while routed into one of them.
// /snop is deliberately excluded: that remote (snop_frontend) fetches its
// own active snop already, fetching it here too would just duplicate it.
const SNOP_MODULE_PATH_PREFIXES = ["/dp", "/snoe", "/ds", "/snp"];

const withCustomPermissions = (profile) => {
  if (!profile || profile.custom_permissions != null) return profile;
  const stored = readAppValue("user")?.custom_permissions ?? readAppValue("custom_permissions");
  return stored ? { ...profile, custom_permissions: stored } : profile;
};

// Permissions/roles are per-BU, so derive them for the selected BU. Without a
// buId, userObj falls back to the tenant default BU — which, for a user whose
// selected BU isn't the default, exposed the default BU's panes/routes instead
// (e.g. Supply instead of Demand) and made PrivateRoute reject the selected
// BU's own routes.
const deriveFromProfile = (rawProfile, buId) => {
  const profile = withCustomPermissions(rawProfile);
  const processedUser = profile ? userObj(profile, buId) : profile;
  return {
    user: processedUser,
    permissions: processedUser?.permissions ?? [],
    roles: processedUser?.roles ?? [],
    tenantId: profile?.tenant_id ?? null,
    businessUnits: profile?.business_units ?? [],
    customPermissions: profile?.custom_permissions ?? {},
    isAuthenticated: !!profile && Object.keys(profile).length > 0,
  };
};

// "settings" lives in localStorage (see @3sc/common-component's runtime
// store), so it has to be read back from there — not reset to the default —
// or the syncLocalStore effect below would overwrite the user's stored
// settings with DEFAULT_SETTINGS on the very first render.
const readStoredSettings = () => {
  const stored = getRuntimeValue("settings");
  return stored && Object.keys(stored).length ? stored : null;
};

const readLegacySnapshot = () => ({
  ...deriveFromProfile(readAppValue("user")),
  selectedBusinessUnit: readAppValue("selected_business_unit"),
  settings: readStoredSettings() ?? DEFAULT_SETTINGS,
  i18nextLng: readAppValue("i18nextLng"),
});

const resolveBuId = (bu) => bu?.business_unit_id ?? bu?.id;

const BU_STORAGE_KEY = "selected_business_unit_id";
const getPersistedBuId = () => {
  try {
    const raw = sessionStorage.getItem(BU_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setPersistedBuId = (buId) => {
  try {
    if (buId == null) sessionStorage.removeItem(BU_STORAGE_KEY);
    else sessionStorage.setItem(BU_STORAGE_KEY, JSON.stringify(buId));
  } catch {
  }
};

const loadSettingsAndBu = async (profile) => {
  // A refresh (page reload, or the initial mount effect below) re-derives BU
  // from the fresh profile every time. Always taking the default BU here
  // silently discarded whatever BU the user had actively switched to earlier
  // in the session — persisted via the effect below — so every reload
  // snapped back to the default. Prefer the persisted selection whenever
  // it's still one of this profile's business units.
  const persistedId = readAppValue("selected_business_unit")?.business_unit_id ?? getPersistedBuId();
  const businessUnits = profile?.business_units || [];
  const persistedBu =
    persistedId != null ? businessUnits.find((item) => resolveBuId(item) === persistedId) : null;
  const bu = persistedBu
    ? { business_unit_id: resolveBuId(persistedBu), business_unit_name: persistedBu.business_unit_name ?? persistedBu.name }
    : findDefaultBu(businessUnits);
  if (!profile?.tenant_id || !bu?.business_unit_id) {
    return { selectedBusinessUnit: null, settings: readStoredSettings() ?? DEFAULT_SETTINGS };
  }

  // Fire-and-forget (best-effort — see comment above). Awaiting a rejected,
  // un-awaited promise inside a try/catch never actually catches it, so attach
  // a .catch() to swallow failures instead.
  getConfiguration(profile.access, profile.tenant_id, bu.business_unit_id).catch(() => {});

  // The shared runtime store keeps settings in memory only — in this
  // @3sc/common-component version setSharedRuntimeContext never writes them to
  // localStorage — so a page refresh resets the store to its bootstrap default
  // ({ platform: { theme } }). Reading that copy here treated the default as the
  // user's settings and dropped every per-BU preference on refresh (theme,
  // thousand style, grid columns, ...). The backend is the real source of truth
  // (updateSettings writes each change there), so fetch it on load and prefer it,
  // falling back to whatever is in the store — or the default — only when the
  // fetch is empty or fails.
  try {
    const settingsRes = await getUserSettings(null, profile.tenant_id, bu.business_unit_id);
    const received = settingsRes?.data?.responseData?.response?.settings;
    if (received && Object.keys(received).length) {
      return { selectedBusinessUnit: bu, settings: received };
    }
    return { selectedBusinessUnit: bu, settings: readStoredSettings() ?? DEFAULT_SETTINGS };
  } catch {
    return { selectedBusinessUnit: bu, settings: readStoredSettings() ?? DEFAULT_SETTINGS };
  }
};

const syncLocalStore = (snapshot) => {
  const patch = {
    user: snapshot.user,
    selected_business_unit: snapshot.selectedBusinessUnit,
    settings: snapshot.settings,
  };
  setAppRuntimeContext(patch);
  setSharedRuntimeContext(patch);
};

export function AuthProvider({ children }) {
  const [state, setState] = useState(() => {
    const snapshot = readLegacySnapshot();
    return { ...snapshot, isLoading: true };
  });

  const applyProfile = useCallback(async (profile) => {
    const { selectedBusinessUnit, settings } = profile
      ? await loadSettingsAndBu(profile)
      : { selectedBusinessUnit: null, settings: DEFAULT_SETTINGS };
    const base = deriveFromProfile(profile, selectedBusinessUnit?.business_unit_id);
    setState((prev) => ({ ...prev, ...base, selectedBusinessUnit, settings, isLoading: false }));
  }, []);

  // Synchronous counterpart to applyProfile: processLogin (commonHelper.js,
  // a plain helper, not a hook) already knows the freshly-logged-in profile
  // and its resolved BU/settings from its own login-response handling, and
  // already writes them to the legacy runtime-context/shared-store (via
  // setUserCookies) — but that never touched this Context's own React state,
  // which stayed on its pre-login (unauthenticated) snapshot until a full
  // page reload re-ran refresh(). In the meantime, AuthRouter (which reads
  // the legacy store directly) saw the fresh login and redirected forward,
  // while PrivateRoute (reading this Context) still saw isAuthenticated:
  // false and redirected back to login — an infinite flicker between the
  // two. hydrate() updates this Context's state immediately, without
  // re-deriving a possibly different default BU via loadSettingsAndBu.
  const hydrate = useCallback((profile, { selectedBusinessUnit = null, settings = DEFAULT_SETTINGS } = {}) => {
    const base = deriveFromProfile(profile, selectedBusinessUnit?.business_unit_id);
    setState((prev) => ({ ...prev, ...base, selectedBusinessUnit, settings, isLoading: false }));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const profile = await getSessionProfile();
      if (profile) {
        await applyProfile(profile);
        return;
      }
      setState((prev) => ({ ...prev, isLoading: false }));
    } catch {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [applyProfile]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // syncLocalStore mirrors this Context's state into the legacy
  // runtime-context/shared-store for code that still reads it directly
  // (AuthRouter, appState consumers, other federated remotes). This used to
  // run inline inside each setState(prev => {...}) updater above — but
  // React invokes updater functions during render, and setAppRuntimeContext/
  // setSharedRuntimeContext synchronously notify every subscribeRuntimeContext
  // listener, including ones elsewhere that call setState on a *different*
  // component (e.g. useMenuData's runtimeVersion counter, feeding SideNav) —
  // React rejects that ("Cannot update a component while rendering a
  // different component"). Doing the sync here instead, in its own effect
  // keyed on the exact fields it needs, keeps every setState updater above
  // pure and defers the store write to after commit, where side effects
  // belong.
  useEffect(() => {
    syncLocalStore({
      user: state.user,
      selectedBusinessUnit: state.selectedBusinessUnit,
      settings: state.settings,
    });
    if (!state.isLoading) {
      setPersistedBuId(state.selectedBusinessUnit?.business_unit_id ?? null);
    }
  }, [state.user, state.selectedBusinessUnit, state.settings, state.isLoading]);

  useEffect(() => {
    const sync = () => {
      setState((prev) => {
        const lng = getRuntimeValue("i18nextLng");
        return prev.i18nextLng === lng ? prev : { ...prev, i18nextLng: lng };
      });
    };
    sync();
    return subscribeRuntimeContext(sync);
  }, []);

  const location = useLocation();
  const isSnopModule = SNOP_MODULE_PATH_PREFIXES.some((prefix) =>
    location.pathname?.startsWith(prefix)
  );

  useEffect(() => {
    const tenantId = state.tenantId;
    const buId = state.selectedBusinessUnit?.business_unit_id;
    if (!tenantId || !buId || !isSnopModule) return;
    getActiveSnop(tenantId, buId);
  }, [state.tenantId, state.selectedBusinessUnit?.business_unit_id, isSnopModule]);

  useEffect(() => {
    const onLogout = () => applyProfile(null);
    globalThis.addEventListener("logout", onLogout);
    return () => globalThis.removeEventListener("logout", onLogout);
  }, [applyProfile]);

  useEffect(() => {
    const onLogin = (event) => {
      const { profile, selectedBusinessUnit, settings } = event?.detail || {};
      hydrate(profile, { selectedBusinessUnit, settings });
    };
    globalThis.addEventListener("login", onLogin);
    return () => globalThis.removeEventListener("login", onLogin);
  }, [hydrate]);

  // Session upkeep (idle logout, periodic token refresh, INVALID_TOKEN
  // handling) used to live inside PrivateRoute — but PrivateRoute is
  // recreated fresh on every route change (each distinct path is its own
  // <Route element={<PrivateRoute .../>}>), so all of this was tearing down
  // and restarting on every navigation, including a redundant /token/verify
  // round-trip that briefly rendered nothing valid yet (a flash of the
  // error/login redirect) before the fresh check resolved. AuthProvider
  // mounts exactly once, so anchoring it here instead means PrivateRoute can
  // read already-known, stable auth state on every render — no per-route
  // async gap, no redundant network calls.
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const resetSettings = useCallback((refreshError) => {
    sessionStorage.clear();
    setSharedRuntimeContext({ settings: null });
    navigate(ROUTES_PATH.ROUTE);
    handleAuthError(refreshError || "SESSION_EXPIRED", navigate, dispatch);
  }, [navigate, dispatch]);

  useTabScopedIdleLogout({
    logoutFn: resetSettings,
    idleMs: 29 * 60 * 1000 + 30 * 1000, // ~30 minutes
    countNetworkActivityInThisTab: false, // change to true if you want network calls to reset timer
  });

  const reVerifyToken = useCallback(async () => {
    if (!state.user) return;
    try {
      const authenticator = sessionStorage.getItem("authenticator");
      await refreshAccessToken(null, authenticator);
    } catch (refreshError) {
      resetSettings(refreshError);
    }
  }, [state.user, resetSettings]);

  useEffect(() => {
    const handleTokenEvent = (event) => {
      if (event?.detail?.isInvalid) reVerifyToken();
    };
    globalThis.addEventListener(ENUM_EVENT.INVALID_TOKEN, handleTokenEvent);
    return () => globalThis.removeEventListener(ENUM_EVENT.INVALID_TOKEN, handleTokenEvent);
  }, [reVerifyToken]);

  useEffect(() => {
    const interval = setInterval(() => {
      reVerifyToken();
    }, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [reVerifyToken]);

  // Re-derive user/permissions/roles for the new BU too — otherwise they stay
  // on the previous BU and the syncLocalStore effect writes them back over the
  // BU-specific user MultiAvatar stores, so the new BU's routes 404.
  const setSelectedBusinessUnit = useCallback((businessUnit) => {
    setState((prev) => ({
      ...prev,
      ...(prev.user ? deriveFromProfile(prev.user, businessUnit?.business_unit_id) : {}),
      selectedBusinessUnit: businessUnit,
    }));
  }, []);

  const setSettings = useCallback((settings) => {
    setState((prev) => ({ ...prev, settings }));
  }, []);

  const contextValue = useMemo(
    () => ({ ...state, refresh, applyProfile, hydrate, setSelectedBusinessUnit, setSettings }),
    [state, refresh, applyProfile, hydrate, setSelectedBusinessUnit, setSettings]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx) return ctx;

  const snapshot = readLegacySnapshot();
  return {
    ...snapshot,
    isLoading: false,
    refresh: async () => {},
    applyProfile: () => {},
    hydrate: () => {},
    setSelectedBusinessUnit: () => {},
    setSettings: () => {},
  };
}
